import { effect, Injectable, signal } from '@angular/core';

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { environment } from 'src/environments/environment';

import { Utility } from '../utility.service';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private readonly hubConnection: HubConnection = this.buildHubConnection();

  private readonly _state = signal<HubConnectionState | undefined>(undefined);
  public readonly state = this._state.asReadonly();

  public get connectionId() {
    return this.hubConnection.connectionId;
  }

  private reconnectionTimer?: number;
  private retryCount: number = 0;
  private elapsedMsOnReconnect: number = 0;
  private readonly maxElapsedMsOnReconnect: number = 1000 * 60 * 60;

  private isStoppedByUser: boolean = false;

  constructor() {
    this.hubConnection.onclose((error) => {
      if (this.isStoppedByUser) {
        this._state.set(HubConnectionState.Disconnected);
      } else {
        console.error(error);
        this.reconnect();
      }
    });

    effect(() => {
      const state = this.state();

      let stateToLog: string | undefined;

      switch (state) {
        case HubConnectionState.Connected:
          stateToLog = 'Connected.';

          this.retryCount = 0;
          this.elapsedMsOnReconnect = 0;
          break;

        case HubConnectionState.Reconnecting:
          stateToLog = 'Disconnected. Trying to reconnect...';
          break;

        case HubConnectionState.Disconnected:
          stateToLog = 'Disconnected.';

          this.retryCount = 0;
          this.elapsedMsOnReconnect = 0;
          break;
      }

      if (stateToLog) {
        console.info('%c' + 'SignalR', 'font-weight: bold', stateToLog);
      }
    });
  }

  private buildHubConnection(): HubConnection {
    const hubConnection = new HubConnectionBuilder()
      .withUrl(environment.newSignalRUrl, {
        accessTokenFactory: () => Utility.authToken!,
      })
      .configureLogging(LogLevel.Critical)
      .build();

    return hubConnection;
  }

  public async start(): Promise<void> {
    if (this.hubConnection.state !== HubConnectionState.Disconnected) {
      return;
    }

    this.isStoppedByUser = false;

    if (this.state() !== HubConnectionState.Reconnecting) {
      this._state.set(HubConnectionState.Connecting);
    }

    try {
      await this.hubConnection.start();
      this._state.set(HubConnectionState.Connected);
    } catch (error) {
      this.reconnect();

      throw error;
    }
  }

  public async stop(isStoppedByUser: boolean = true): Promise<void> {
    this.isStoppedByUser = isStoppedByUser;

    if (
      this.hubConnection.state === HubConnectionState.Disconnected ||
      this.hubConnection.state === HubConnectionState.Disconnecting
    ) {
      this._state.set(HubConnectionState.Disconnected);
      return;
    }

    if (this.state() === HubConnectionState.Reconnecting) {
      this._state.set(HubConnectionState.Disconnected);
      window.clearTimeout(this.reconnectionTimer);
      return;
    }

    try {
      await this.hubConnection.stop();
    } finally {
      this._state.set(HubConnectionState.Disconnected);
    }
  }

  private reconnect(): void {
    this._state.set(HubConnectionState.Reconnecting);

    const nextRetryInMs: number = this.nextRetryInMs(this.retryCount++);

    this.reconnectionTimer = window.setTimeout(() => {
      this.elapsedMsOnReconnect += nextRetryInMs;

      if (
        this.elapsedMsOnReconnect + nextRetryInMs >
        this.maxElapsedMsOnReconnect
      ) {
        this.stop(false);
        return;
      }

      this.start();
    }, nextRetryInMs);
  }

  private nextRetryInMs(retryCount: number): number {
    const baseDelayMs: number = 1000 * 2; // 2s
    const maxDelayMs: number = 1000 * 60; // 60s

    const base = Math.min(baseDelayMs * 2 ** retryCount, maxDelayMs); // 2s...60s
    const jitter = Math.floor(Math.random() * 501) - 250; // -250ms...250ms

    return Math.max(0, base + jitter);
  }

  public on<T>(eventName: EventName, handler: (payload: T) => void): void {
    this.hubConnection.on(eventName, handler);
  }

  public off(eventName: EventName): void {
    this.hubConnection.off(eventName);
  }
}

type EventName =
  | 'MonthlyReportProgress'
  | 'MonthlyReportReady'
  | 'MonthlyReportNoData'
  | 'MonthlyReportFailed'
  | 'AccountSheetProgress'
  | 'AccountSheetReady'
  | 'AccountSheetNoData'
  | 'AccountSheetFailed';
